import React, { useState, useEffect } from 'react';
import { Sparkles, Wrench, Save, RotateCcw, Plus, Trash2, Edit2, ChevronDown, ChevronUp, Info, Sliders } from 'lucide-react';
import { API_URL } from '../../config';

export default function ModiSettings({ adminToken }) {
  const [selectedVersion, setSelectedVersion] = useState('v1');
  const [selectedMode, setSelectedMode] = useState('modify');
  const [modifyConfig, setModifyConfig] = useState(null);
  const [remixConfig, setRemixConfig] = useState(null);
  const [modelOptions, setModelOptions] = useState(null); // NEU: Model Options State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    loadConfigs(selectedVersion);
  }, [selectedVersion]);

  const loadConfigs = async (version) => {
    try {
      setLoading(true);
      
      // Lade alle 3 Configs
      const [modifyRes, remixRes, modelRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/modes/modify/${version}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }),
        fetch(`${API_URL}/api/admin/modes/remix/${version}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }),
        fetch(`${API_URL}/api/admin/modes/model-options/${version}`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        })
      ]);

      if (!modifyRes.ok || !remixRes.ok || !modelRes.ok) throw new Error('Failed to load configs');

      const modifyData = await modifyRes.json();
      const remixData = await remixRes.json();
      const modelData = await modelRes.json();
      
      setModifyConfig(modifyData);
      setRemixConfig(remixData);
      setModelOptions(modelData);
      setIsEditing(false);
      
    } catch (error) {
      console.error('Error loading configs:', error);
      showMessage('Fehler beim Laden der Konfigurationen', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      
      let config, endpoint;
      
      if (selectedMode === 'modify') {
        config = modifyConfig;
        endpoint = `${API_URL}/api/admin/modes/modify/${selectedVersion}`;
      } else if (selectedMode === 'remix') {
        config = remixConfig;
        endpoint = `${API_URL}/api/admin/modes/remix/${selectedVersion}`;
      } else if (selectedMode === 'models') {
        config = modelOptions;
        endpoint = `${API_URL}/api/admin/modes/model-options/${selectedVersion}`;
      }
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(config)
      });

      if (!res.ok) throw new Error('Failed to save config');

      showMessage('✅ Konfiguration gespeichert!', 'success');
      setIsEditing(false);
      await loadConfigs(selectedVersion);
      
    } catch (error) {
      console.error('Error saving config:', error);
      showMessage('❌ Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const updateModifyField = (field, value) => {
    if (!isEditing) return;
    setModifyConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateRemixField = (field, value) => {
    if (!isEditing) return;
    setRemixConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateModelOptions = (category, newOptions) => {
    if (!isEditing) return;
    setModelOptions(prev => ({ ...prev, [category]: newOptions }));
  };

  const addModelOption = (category) => {
    if (!isEditing) return;
    const newOption = {
      value: category === 'base_tune_options' ? 0 : '',
      label: 'Neues Modell',
      description: '',
      recommended: false,
      order: (modelOptions[category]?.length || 0) + 1
    };
    setModelOptions(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), newOption]
    }));
  };

  const updateModelOption = (category, index, field, value) => {
    if (!isEditing) return;
    setModelOptions(prev => ({
      ...prev,
      [category]: prev[category].map((opt, idx) =>
        idx === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const deleteModelOption = (category, index) => {
    if (!isEditing) return;
    if (!window.confirm('Option wirklich löschen?')) return;
    setModelOptions(prev => ({
      ...prev,
      [category]: prev[category].filter((_, idx) => idx !== index)
    }));
  };

  const addCategory = () => {
    if (!isEditing) return;
    const newCategory = {
      id: `cat_${Date.now()}`,
      label: 'NEUE KATEGORIE',
      label_color: '#06B6D4',
      icon: 'Sparkles',
      order: (modifyConfig.categories?.length || 0) + 1,
      visible: true,
      buttons: []
    };
    setModifyConfig(prev => ({
      ...prev,
      categories: [...(prev.categories || []), newCategory]
    }));
  };

  const updateCategory = (categoryId, field, value) => {
    if (!isEditing) return;
    setModifyConfig(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const deleteCategory = (categoryId) => {
    if (!isEditing) return;
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    setModifyConfig(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat.id !== categoryId)
    }));
  };

  const addButton = (categoryId) => {
    if (!isEditing) return;
    const newButton = {
      id: `btn_${Date.now()}`,
      label: 'Neuer Button',
      icon: null,
      prompt_addition: '',
      order: 0
    };
    setModifyConfig(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId
          ? { ...cat, buttons: [...cat.buttons, newButton] }
          : cat
      )
    }));
  };

  const updateButton = (categoryId, buttonId, field, value) => {
    if (!isEditing) return;
    setModifyConfig(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId
          ? {
              ...cat,
              buttons: cat.buttons.map(btn =>
                btn.id === buttonId ? { ...btn, [field]: value } : btn
              )
            }
          : cat
      )
    }));
  };

  const deleteButton = (categoryId, buttonId) => {
    if (!isEditing) return;
    if (!window.confirm('Button wirklich löschen?')) return;
    setModifyConfig(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId
          ? { ...cat, buttons: cat.buttons.filter(btn => btn.id !== buttonId) }
          : cat
      )
    }));
  };

  if (loading || !modifyConfig || !remixConfig) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white/70">Lade Modi-Einstellungen...</p>
        </div>
      </div>
    );
  }

  const config = selectedMode === 'modify' ? modifyConfig : remixConfig;
  
  if (!config) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">❌ Fehler: Config konnte nicht geladen werden</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black grain-texture p-2 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-[#10B981]" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Modi-Verwaltung</h1>
                <p className="text-white/60 text-xs sm:text-sm mt-1">
                  Vollständige Kontrolle über Remix & Modify Modi
                </p>
              </div>
            </div>
            
            {/* Version & Modus Selector */}
            <div className="flex items-center gap-3">
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                disabled={isEditing}
                className="px-3 sm:px-4 py-2.5 sm:py-2 bg-black/60 border border-white/20 rounded-xl text-white font-semibold focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-50 text-sm sm:text-base"
                data-testid="version-select"
              >
                <option value="v1">V1 (ID-Card)</option>
                <option value="v2">V2 (Snapshot Studio)</option>
              </select>
              
              <select
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                disabled={isEditing}
                className="px-3 sm:px-4 py-2.5 sm:py-2 bg-black/60 border border-white/20 rounded-xl text-white font-semibold focus:border-[#10B981] focus:outline-none transition-all disabled:opacity-50 text-sm sm:text-base"
                data-testid="mode-select"
              >
                <option value="modify">Modify-Modus</option>
                <option value="remix">Remix-Modus</option>
                <option value="models">Modell-Verwaltung</option>
              </select>
            </div>
          </div>
        </div>

        {/* Message Toast */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border ${
            message.type === 'success' 
              ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center justify-center gap-2 px-6 py-3.5 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all"
              data-testid="edit-btn"
            >
              <Edit2 className="w-5 h-5" />
              Bearbeiten
            </button>
          ) : (
            <>
              <button
                onClick={saveConfig}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-xl transition-all disabled:opacity-50"
                data-testid="save-btn"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Speichere...' : 'Speichern'}
              </button>

              <button
                onClick={() => {
                  setIsEditing(false);
                  loadConfigs(selectedVersion);
                }}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/20 font-semibold rounded-xl transition-all disabled:opacity-50"
              >
                <RotateCcw className="w-5 h-5" />
                Abbrechen
              </button>
            </>
          )}
        </div>

        {/* Content basierend auf Modus */}
        {selectedMode === 'modify' ? (
          <ModifyModeEditor
            config={modifyConfig}
            isEditing={isEditing}
            updateField={updateModifyField}
            addCategory={addCategory}
            updateCategory={updateCategory}
            deleteCategory={deleteCategory}
            addButton={addButton}
            updateButton={updateButton}
            deleteButton={deleteButton}
            expandedCategory={expandedCategory}
            setExpandedCategory={setExpandedCategory}
          />
        ) : selectedMode === 'remix' ? (
          <RemixModeEditor
            config={remixConfig}
            isEditing={isEditing}
            updateField={updateRemixField}
          />
        ) : (
          <ModelOptionsEditor
            config={modelOptions}
            isEditing={isEditing}
            addOption={addModelOption}
            updateOption={updateModelOption}
            deleteOption={deleteModelOption}
          />
        )}
      </div>
    </div>
  );
}

// Model Options Editor Component
function ModelOptionsEditor({ config, isEditing, addOption, updateOption, deleteOption }) {
  if (!config) return null;
  
  const categories = [
    { key: 'base_tune_options', label: 'Base Models (Flux, SDXL, etc.)', type: 'number' },
    { key: 'model_type_options', label: 'Model Types (LoRA, Dreambooth, etc.)', type: 'string' },
    { key: 'preset_options', label: 'Training Presets', type: 'string' },
    { key: 'branch_options', label: 'Branches (Flux1, SD1.5, etc.)', type: 'string' },
    { key: 'scheduler_options', label: 'Scheduler', type: 'string' },
    { key: 'style_options', label: 'Styles', type: 'string' },
    { key: 'color_grading_options', label: 'Color Grading', type: 'string' },
    { key: 'aspect_ratio_options', label: 'Aspect Ratios', type: 'string' },
    { key: 'controlnet_options', label: 'ControlNet', type: 'string' },
  ];
  
  return (
    <div className="space-y-6">
      {categories.map((category) => (
        <div key={category.key} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{category.label}</h2>
            {isEditing && (
              <button
                onClick={() => addOption(category.key)}
                className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-lg transition-all"
              >
                <Plus className="w-4 h-4" />
                Hinzufügen
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            {(config[category.key] || [])
              .sort((a, b) => a.order - b.order)
              .map((option, index) => (
                <div key={index} className="p-4 bg-white/5 border border-white/10 rounded-xl">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-3">
                      <label className="block text-xs text-white/60 mb-1">Wert {category.type === 'number' && '(ID)'}</label>
                      <input
                        type={category.type === 'number' ? 'number' : 'text'}
                        value={option.value}
                        onChange={(e) => updateOption(category.key, index, 'value', category.type === 'number' ? parseInt(e.target.value) : e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-5">
                      <label className="block text-xs text-white/60 mb-1">Label / Anzeigename</label>
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => updateOption(category.key, index, 'label', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs text-white/60 mb-1">Reihenfolge</label>
                      <input
                        type="number"
                        value={option.order}
                        onChange={(e) => updateOption(category.key, index, 'order', parseInt(e.target.value))}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-1 flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={option.recommended || false}
                          onChange={(e) => updateOption(category.key, index, 'recommended', e.target.checked)}
                          disabled={!isEditing}
                          className="w-4 h-4"
                        />
                        <span className="text-xs text-white/60">⭐</span>
                      </label>
                    </div>
                    
                    <div className="md:col-span-1 flex items-end justify-end">
                      {isEditing && (
                        <button
                          onClick={() => deleteOption(category.key, index)}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Modify-Modus Editor Component
function ModifyModeEditor({
  config,
  isEditing,
  updateField,
  addCategory,
  updateCategory,
  deleteCategory,
  addButton,
  updateButton,
  deleteButton,
  expandedCategory,
  setExpandedCategory
}) {
  // Lade Model Options für Dropdowns
  const [modelOptions, setModelOptions] = React.useState(null);
  
  const loadModelOptions = React.useCallback(async () => {
    if (!config?.version) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/modes/public/model-options/${config.version}`);
      if (res.ok) {
        const data = await res.json();
        setModelOptions(data);
      }
    } catch (error) {
      console.error('Error loading model options:', error);
    }
  }, [config]);
  
  React.useEffect(() => {
    loadModelOptions();
  }, [loadModelOptions]);
  
  if (!config) return null;
  if (!modelOptions) return <div className="text-white">Lade Optionen...</div>;
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Header-Einstellungen</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Titel</label>
            <input
              type="text"
              value={config.header_title}
              onChange={(e) => updateField('header_title', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Untertitel</label>
            <input
              type="text"
              value={config.header_subtitle}
              onChange={(e) => updateField('header_subtitle', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Icon (Lucide Icon Name)</label>
            <input
              type="text"
              value={config.header_icon}
              onChange={(e) => updateField('header_icon', e.target.value)}
              disabled={!isEditing}
              placeholder="z.B. Wrench, Sparkles, Settings"
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Kategorien */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Kategorien</h2>
          {isEditing && (
            <button
              onClick={addCategory}
              className="flex items-center gap-2 px-4 py-2 bg-[#10B981] hover:bg-[#0EA574] text-black font-semibold rounded-lg transition-all"
            >
              <Plus className="w-4 h-4" />
              Kategorie hinzufügen
            </button>
          )}
        </div>

        <div className="space-y-4">
          {config.categories?.map((category) => (
            <CategoryEditor
              key={category.id}
              category={category}
              isEditing={isEditing}
              updateCategory={updateCategory}
              deleteCategory={deleteCategory}
              addButton={addButton}
              updateButton={updateButton}
              deleteButton={deleteButton}
              isExpanded={expandedCategory === category.id}
              onToggle={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
            />
          ))}
        </div>
      </div>

      {/* Freitext & Info Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Weitere Einstellungen</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Freitext-Label</label>
            <input
              type="text"
              value={config.freetext_label}
              onChange={(e) => updateField('freetext_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Freitext-Placeholder</label>
            <input
              type="text"
              value={config.freetext_placeholder}
              onChange={(e) => updateField('freetext_placeholder', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Info-Box Titel</label>
            <input
              type="text"
              value={config.info_title}
              onChange={(e) => updateField('info_title', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Info-Box Text</label>
            <textarea
              value={config.info_text}
              onChange={(e) => updateField('info_text', e.target.value)}
              disabled={!isEditing}
              rows={3}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 resize-none"
            />
          </div>
        </div>
      </div>

      {/* ASTRIA-PARAMETER FÜR MODIFY */}
      <div className="bg-gradient-to-br from-[#10B981]/10 to-transparent border-2 border-[#10B981]/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
            <Sliders className="w-5 h-5 text-[#10B981]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">⚙️ Astria-Einstellungen für MODIFY</h2>
            <p className="text-white/60 text-sm">Separate Parameter nur für Modify-Generierungen</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Base Model - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Base Model</label>
            <select
              value={config.base_tune_id}
              onChange={(e) => updateField('base_tune_id', parseInt(e.target.value))}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.base_tune_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} {opt.recommended ? '⭐' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Model Type - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Model Type</label>
            <select
              value={config.model_type}
              onChange={(e) => updateField('model_type', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.model_type_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Preset - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Preset</label>
            <select
              value={config.preset}
              onChange={(e) => updateField('preset', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.preset_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Branch - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
            <select
              value={config.branch}
              onChange={(e) => updateField('branch', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.branch_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* CFG Scale */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">CFG Scale: {config.cfg_scale}</label>
            <input
              type="range"
              min="1"
              max="20"
              step="0.1"
              value={config.cfg_scale}
              onChange={(e) => updateField('cfg_scale', parseFloat(e.target.value))}
              disabled={!isEditing}
              className="w-full disabled:opacity-60"
            />
          </div>

          {/* Steps */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Steps: {config.steps}</label>
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              value={config.steps}
              onChange={(e) => updateField('steps', parseInt(e.target.value))}
              disabled={!isEditing}
              className="w-full disabled:opacity-60"
            />
          </div>

          {/* Super Resolution */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.super_resolution}
              onChange={(e) => updateField('super_resolution', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Super Resolution</label>
          </div>

          {/* Face Correct */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.face_correct}
              onChange={(e) => updateField('face_correct', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Face Correct</label>
          </div>

          {/* Hires Fix */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.hires_fix}
              onChange={(e) => updateField('hires_fix', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Hires Fix</label>
          </div>
        </div>

        <div className="mt-4 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
          <p className="text-[#10B981] text-sm">
            💡 Diese Einstellungen gelten NUR für Modify-Generierungen. Normale Shots nutzen die Einstellungen aus &quot;Astria Tuning&quot;.
          </p>
        </div>
      </div>
    </div>
  );
}

// Category Editor Component
function CategoryEditor({
  category,
  isEditing,
  updateCategory,
  deleteCategory,
  addButton,
  updateButton,
  deleteButton,
  isExpanded,
  onToggle
}) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      {/* Category Header */}
      <div
        onClick={onToggle}
        className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 cursor-pointer transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="text-2xl">{category.icon || '✨'}</div>
          <div>
            <h3 className="text-white font-bold">{category.label}</h3>
            <p className="text-white/50 text-sm">{category.buttons?.length || 0} Buttons</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteCategory(category.id);
              }}
              className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
        </div>
      </div>

      {/* Category Content (Expanded) */}
      {isExpanded && (
        <div className="p-4 bg-black/20 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
              <input
                type="text"
                value={category.label}
                onChange={(e) => updateCategory(category.id, 'label', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Icon (Lucide)</label>
              <input
                type="text"
                value={category.icon || ''}
                onChange={(e) => updateCategory(category.id, 'icon', e.target.value)}
                disabled={!isEditing}
                placeholder="z.B. Scissors, Shirt, User"
                className="w-full px-3 py-2 bg-black/60 border border-white/20 rounded-lg text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 text-sm"
              />
            </div>
          </div>

          {/* Buttons */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-white/80">Buttons</label>
              {isEditing && (
                <button
                  onClick={() => addButton(category.id)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#10B981] hover:bg-[#0EA574] text-black text-sm font-semibold rounded-lg transition-all"
                >
                  <Plus className="w-3 h-3" />
                  Button
                </button>
              )}
            </div>

            <div className="space-y-3">
              {category.buttons?.map((button) => (
                <div key={button.id} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-white/60 mb-1">Label</label>
                      <input
                        type="text"
                        value={button.label}
                        onChange={(e) => updateButton(category.id, button.id, 'label', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-2 py-1.5 bg-black/60 border border-white/20 rounded text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs text-white/60 mb-1">Prompt Addition</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={button.prompt_addition}
                          onChange={(e) => updateButton(category.id, button.id, 'prompt_addition', e.target.value)}
                          disabled={!isEditing}
                          placeholder="z.B. shorter hair, modern haircut"
                          className="flex-1 px-2 py-1.5 bg-black/60 border border-white/20 rounded text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                        />
                        {isEditing && (
                          <button
                            onClick={() => deleteButton(category.id, button.id)}
                            className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Remix-Modus Editor Component (VOLLSTÄNDIG)
function RemixModeEditor({ config, isEditing, updateField }) {
  // Lade Model Options für Dropdowns
  const [modelOptions, setModelOptions] = React.useState(null);
  
  const loadModelOptions = React.useCallback(async () => {
    if (!config?.version) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/modes/public/model-options/${config.version}`);
      if (res.ok) {
        const data = await res.json();
        setModelOptions(data);
      }
    } catch (error) {
      console.error('Error loading model options:', error);
    }
  }, [config]);
  
  React.useEffect(() => {
    loadModelOptions();
  }, [loadModelOptions]);
  
  if (!config) return null;
  if (!modelOptions) return <div className="text-white">Lade Optionen...</div>;
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#10B981]" />
          Header-Einstellungen
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Titel</label>
            <input
              type="text"
              value={config.header_title}
              onChange={(e) => updateField('header_title', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Untertitel</label>
            <input
              type="text"
              value={config.header_subtitle}
              onChange={(e) => updateField('header_subtitle', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Icon (Lucide Icon Name)</label>
            <input
              type="text"
              value={config.header_icon}
              onChange={(e) => updateField('header_icon', e.target.value)}
              disabled={!isEditing}
              placeholder="z.B. Sparkles, Zap, Wand2"
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Original-Bild Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Original-Bild Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.original_image_label}
              onChange={(e) => updateField('original_image_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Badge-Text</label>
            <input
              type="text"
              value={config.original_image_badge_text}
              onChange={(e) => updateField('original_image_badge_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Original Prompt Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Original Prompt Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.original_prompt_label}
              onChange={(e) => updateField('original_prompt_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Text wenn leer</label>
            <input
              type="text"
              value={config.original_prompt_empty_text}
              onChange={(e) => updateField('original_prompt_empty_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Remix Prompt Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Remix Prompt Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.remix_prompt_label}
              onChange={(e) => updateField('remix_prompt_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Hinweis-Text</label>
            <textarea
              value={config.remix_prompt_hint}
              onChange={(e) => updateField('remix_prompt_hint', e.target.value)}
              disabled={!isEditing}
              rows={2}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Placeholder-Text</label>
            <input
              type="text"
              value={config.remix_prompt_placeholder}
              onChange={(e) => updateField('remix_prompt_placeholder', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Settings Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Settings Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.settings_label}
              onChange={(e) => updateField('settings_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Anzahl Bilder Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Anzahl Bilder Section</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.num_images_label}
              onChange={(e) => updateField('num_images_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Frage-Text</label>
            <input
              type="text"
              value={config.num_images_question}
              onChange={(e) => updateField('num_images_question', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Slider Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Variation Slider</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Label</label>
            <input
              type="text"
              value={config.slider_label}
              onChange={(e) => updateField('slider_label', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Hinweis: Niedrig (50%)</label>
              <input
                type="text"
                value={config.slider_hint_low}
                onChange={(e) => updateField('slider_hint_low', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Hinweis: Mittel (70%)</label>
              <input
                type="text"
                value={config.slider_hint_mid}
                onChange={(e) => updateField('slider_hint_mid', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Hinweis: Hoch (95%)</label>
              <input
                type="text"
                value={config.slider_hint_high}
                onChange={(e) => updateField('slider_hint_high', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Info-Box Titel</label>
            <input
              type="text"
              value={config.slider_info_title}
              onChange={(e) => updateField('slider_info_title', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Info-Punkte (einer pro Zeile)</label>
            <textarea
              value={config.slider_info_items?.join('\n') || ''}
              onChange={(e) => updateField('slider_info_items', e.target.value.split('\n'))}
              disabled={!isEditing}
              rows={4}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 resize-none font-mono text-sm"
              placeholder="• 50%: Text hier&#10;• 70%: Text hier&#10;• 95%: Text hier"
            />
          </div>
        </div>
      </div>

      {/* Info-Box Section */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Info-Box (unten)</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Titel</label>
            <input
              type="text"
              value={config.info_title}
              onChange={(e) => updateField('info_title', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Text</label>
            <textarea
              value={config.info_text}
              onChange={(e) => updateField('info_text', e.target.value)}
              disabled={!isEditing}
              rows={3}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Button Texte */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Button-Texte</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Abbrechen-Button</label>
            <input
              type="text"
              value={config.button_cancel_text}
              onChange={(e) => updateField('button_cancel_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Generieren-Button (verwende {'{count}'} und {'{variant}'})</label>
            <input
              type="text"
              value={config.button_generate_text}
              onChange={(e) => updateField('button_generate_text', e.target.value)}
              disabled={!isEditing}
              placeholder="z.B. {count} Remix-{variant} erstellen"
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
            <p className="text-xs text-white/50 mt-1">
              {'{count}'} wird mit Anzahl ersetzt, {'{variant}'} mit &quot;Variation&quot; oder &quot;Variationen&quot;
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Während Generierung</label>
            <input
              type="text"
              value={config.button_generating_text}
              onChange={(e) => updateField('button_generating_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Vorlagen-Verwaltung - NEU! */}
      <div className="bg-gradient-to-br from-cyan-500/10 to-transparent border-2 border-cyan-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              Remix-Vorlagen ({config.templates?.length || 0})
            </h2>
            <p className="text-white/60 text-sm mt-1">Inspirationen für kreative Remix-Szenen</p>
          </div>
          {isEditing && (
            <button
              onClick={() => {
                const newTemplate = {
                  id: `r${Date.now()}`,
                  short: 'Neue Vorlage',
                  full: 'Beschreibung hier...',
                  category: 'Allgemein',
                  order: (config.templates?.length || 0) + 1
                };
                updateField('templates', [...(config.templates || []), newTemplate]);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-black font-semibold rounded-lg transition-all"
            >
              <Plus className="w-4 h-4" />
              Vorlage hinzufügen
            </button>
          )}
        </div>

        {/* Vorlagen-Texte */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Button-Text</label>
            <input
              type="text"
              value={config.templates_button_text}
              onChange={(e) => updateField('templates_button_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Modal-Titel</label>
              <input
                type="text"
                value={config.templates_modal_title}
                onChange={(e) => updateField('templates_modal_title', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Modal-Untertitel</label>
              <input
                type="text"
                value={config.templates_modal_subtitle}
                onChange={(e) => updateField('templates_modal_subtitle', e.target.value)}
                disabled={!isEditing}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Info-Text</label>
            <input
              type="text"
              value={config.templates_info_text}
              onChange={(e) => updateField('templates_info_text', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>

        {/* Template-Liste */}
        <div className="mt-6">
          <h3 className="text-white font-semibold mb-3">Vorlagen ({config.templates?.length || 0})</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(config.templates || [])
              .sort((a, b) => a.order - b.order)
              .map((template, index) => (
                <div key={template.id} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-3">
                      <label className="block text-xs text-white/60 mb-1">Kurz-Titel</label>
                      <input
                        type="text"
                        value={template.short}
                        onChange={(e) => {
                          const updated = [...config.templates];
                          updated[index] = { ...updated[index], short: e.target.value };
                          updateField('templates', updated);
                        }}
                        disabled={!isEditing}
                        className="w-full px-2 py-1.5 bg-black/60 border border-white/20 rounded text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-6">
                      <label className="block text-xs text-white/60 mb-1">Vollständiger Text</label>
                      <input
                        type="text"
                        value={template.full}
                        onChange={(e) => {
                          const updated = [...config.templates];
                          updated[index] = { ...updated[index], full: e.target.value };
                          updateField('templates', updated);
                        }}
                        disabled={!isEditing}
                        className="w-full px-2 py-1.5 bg-black/60 border border-white/20 rounded text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs text-white/60 mb-1">Kategorie</label>
                      <input
                        type="text"
                        value={template.category}
                        onChange={(e) => {
                          const updated = [...config.templates];
                          updated[index] = { ...updated[index], category: e.target.value };
                          updateField('templates', updated);
                        }}
                        disabled={!isEditing}
                        className="w-full px-2 py-1.5 bg-black/60 border border-white/20 rounded text-white text-sm focus:border-[#10B981] focus:outline-none disabled:opacity-60"
                      />
                    </div>
                    
                    <div className="md:col-span-1 flex items-end justify-end">
                      {isEditing && (
                        <button
                          onClick={() => {
                            const updated = config.templates.filter((_, i) => i !== index);
                            updateField('templates', updated);
                          }}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* ASTRIA-PARAMETER FÜR REMIX */}
      <div className="bg-gradient-to-br from-[#10B981]/10 to-transparent border-2 border-[#10B981]/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
            <Sliders className="w-5 h-5 text-[#10B981]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">⚙️ Astria-Einstellungen für REMIX</h2>
            <p className="text-white/60 text-sm">Separate Parameter nur für Remix-Generierungen</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Base Model - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Base Model</label>
            <select
              value={config.base_tune_id}
              onChange={(e) => updateField('base_tune_id', parseInt(e.target.value))}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.base_tune_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} {opt.recommended ? '⭐' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Model Type - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Model Type</label>
            <select
              value={config.model_type}
              onChange={(e) => updateField('model_type', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.model_type_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Preset - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Preset</label>
            <select
              value={config.preset}
              onChange={(e) => updateField('preset', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.preset_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Branch - DROPDOWN */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
            <select
              value={config.branch}
              onChange={(e) => updateField('branch', e.target.value)}
              disabled={!isEditing}
              className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none disabled:opacity-60"
            >
              {modelOptions.branch_options?.sort((a, b) => a.order - b.order).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* CFG Scale */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">CFG Scale: {config.cfg_scale}</label>
            <input
              type="range"
              min="1"
              max="20"
              step="0.1"
              value={config.cfg_scale}
              onChange={(e) => updateField('cfg_scale', parseFloat(e.target.value))}
              disabled={!isEditing}
              className="w-full disabled:opacity-60"
            />
          </div>

          {/* Steps */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Steps: {config.steps}</label>
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              value={config.steps}
              onChange={(e) => updateField('steps', parseInt(e.target.value))}
              disabled={!isEditing}
              className="w-full disabled:opacity-60"
            />
          </div>

          {/* Super Resolution */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.super_resolution}
              onChange={(e) => updateField('super_resolution', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Super Resolution</label>
          </div>

          {/* Face Correct */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.face_correct}
              onChange={(e) => updateField('face_correct', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Face Correct</label>
          </div>

          {/* Hires Fix */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={config.hires_fix}
              onChange={(e) => updateField('hires_fix', e.target.checked)}
              disabled={!isEditing}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-white/80">Hires Fix</label>
          </div>
        </div>

        <div className="mt-4 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
          <p className="text-[#10B981] text-sm">
            💡 Diese Einstellungen gelten NUR für Remix-Generierungen. Normale Shots nutzen die Einstellungen aus &quot;Astria Tuning&quot;.
          </p>
        </div>
      </div>
    </div>
  );
}
